import { captureException } from '@sentry/react';
import { useCallback, useMemo, useState } from 'react';

export interface PrinterAsyncAction<TResult = void, TParams = void> {
  isRunning: boolean;
  run(params: TParams): Promise<TResult>;
}

export interface UsePrinterAsyncActionOptions<TResult = void, TParams = void> {
  name: string;
  actionCallback: (params: TParams) => Promise<TResult>;
  onError: (error: unknown) => Promise<TResult> | never;
}

export const usePrinterAsyncAction = <TResult = void, TParams = void>(
  options: UsePrinterAsyncActionOptions<TResult, TParams>,
): PrinterAsyncAction<TResult, TParams> => {
  const [isRunning, setIsRunning] = useState(false);

  const run = useCallback(
    async (params: TParams): Promise<TResult> => {
      setIsRunning(true);
      try {
        return await options.actionCallback(params);
      } catch (error) {
        captureException(error, {
          level: 'warning',
          extra: {
            message: `Failed to run printer action: ${options.name}`,
          },
        });

        console.error(error);

        return await options.onError(error);
      } finally {
        setIsRunning(false);
      }
    },
    [options.actionCallback, options.onError, options.name],
  );

  return useMemo(() => ({ isRunning, run }), [isRunning, run]);
};
